/**
 * Created by orion on 6/21/17.
 *
 * A thin reader over the `viewer` store - see shared/viewerStore. The state it reads is
 * reactive, so a computed property built on any of these getters re-evaluates when the
 * value changes, and a setter called from one instance is seen by every other. Call sites
 * keep building a throwaway `new CurrentUserClass()`; that stays correct, because the
 * object they all read is the one in the store.
 */
import useViewerStore from './viewerStore';

class CurrentUser {
    constructor () {
        // `$state`, not the store itself: a store instance exposes the keys that existed
        // when it was created, so a field the server fills in later - which is what fetching
        // these over the api will do - would read undefined off it forever.
        this.user = useViewerStore().$state;
    }

    /** All writes go through here, so the store stays the only copy of a field. */
    _set (field, value) {
        this.user[field] = value;
    }

    has_permission (code) {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.permissions[code] !== 2;
    }

    confirm_permission (code) {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.permissions[code] === 1;
    }

    is_authenticated () {
        return this.user.is_authenticated;
    }

    is_superuser () {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.is_superuser;
    }

    get is_searchable () {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.is_searchable;
    }

    set is_searchable (val) {
        this._set('is_searchable', val);
    }

    is_ah () {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.is_ah;
    }

    ah_terms () {
        if (!this.is_authenticated()) {
            return {};
        }
        return this.user.ah_terms;
    }

    ah_link () {
        return this.user.ah_link;
    }

    is_rba_scope_admin () {
        if (!this.is_authenticated()) {
            return false;
        }
        return !!this.user.is_rba_scope_admin;
    }

    rba_tables_link () {
        return this.user.rba_tables_link;
    }

    is_circle_user () {
        if (!this.is_authenticated()) {
            return false;
        }
        return !!this.user.is_circle_user;
    }

    is_buddy () {
        if (!this.is_authenticated()) {
            return false;
        }
        return !!this.user.is_buddy;
    }

    is_rba_insights_supervisor () {
        if (!this.is_authenticated()) {
            return false;
        }
        return !!this.user.is_rba_insights_supervisor;
    }

    can_see_insights_link () {
        // a role holder is handed the Insights of the users bound under them in an RBA Table
        // with its Insights switch on - see get_visible_for_stats_insights_roles_users_id_set
        // on the server. Administering such a Table is not part of it: a Table admin manages its
        // bindings and is told nothing about the users in it.
        return this.is_superuser() || this.is_ah() || this.is_buddy() || this.is_rba_insights_supervisor();
    }

    get can_create_diary_proxy_entries () {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.can_create_diary_proxy_entries;
    }

    get id () {
        return this.user.userprofile_id;
    }

    get wiki_id () {
        return this.user.wiki_id;
    }

    get ah_id () {
        return this.user.ah_id;
    }

    get link () {
        return this.user.profile_link;
    }

    get blog_id () {
        return this.user.blog_id;
    }

    get username () {
        return this.user.username;
    }

    get first_name () {
        return this.user.first_name;
    }

    get first_name_or_username () {
        return this.user.first_name || this.user.username;
    }

    set first_name (val) {
        this._set('first_name', val);
    }

    get last_name () {
        return this.user.last_name;
    }

    set last_name (val) {
        this._set('last_name', val);
    }

    get avatar () {
        return this.user.avatar;
    }

    set avatar (val) {
        this._set('avatar', val);
    }

    get email () {
        return this.user.email;
    }

    get dashboard_config () {
        return this.user.dashboard_config;
    }

    get pending_to_delete () {
        return this.user.pending_to_delete;
    }

    set pending_to_delete (val) {
        this._set('pending_to_delete', val);
    }

    get wiki_enabled () {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.wiki_enabled;
    }

    get is_policy_signed () {
        return this.user.is_policy_signed;
    }

    get is_my_story_empty () {
        return this.user.is_my_story_empty;
    }

    get is_my_media_empty () {
        return this.user.is_my_media_empty;
    }

    set is_policy_signed (val) {
        this._set('is_policy_signed', val);
    }

    get visibility_changes_signed () {
        return this.user.visibility_changes_signed;
    }

    get password_marked_as_weak () {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.password_marked_as_weak;
    }

    get first_and_last_name_set_postponed () {
        return this.user.first_and_last_name_set_postponed;
    }

    get fill_profile_story_postponed () {
        return this.user.fill_profile_story_postponed;
    }

    get weak_password_reminder_postponed () {
        if (!this.is_authenticated()) {
            return false;
        }
        return this.user.weak_password_reminder_postponed;
    }

    get add_avatar_postponed () {
        return this.user.add_avatar_postponed;
    }

    set visibility_changes_signed (val) {
        this._set('visibility_changes_signed', val);
    }

}

export default CurrentUser;
export { CurrentUser as CurrentUserClass };
